import path from "node:path";
import { readFile } from "node:fs/promises";

import {
  parsePrimeUiComponentExportManifest,
  parsePrimeUiExportManifest,
} from "../lib/api-v1-contract.js";
import { ensureDir, extractZip, resetDir, writeUtf8 } from "../lib/fs.js";
import type { ProjectDataProvider } from "../sources/project-data-provider.js";
import type {
  PrimeUiToolContext,
  ProjectSyncSource,
} from "../sources/project-sync-source.js";
import { copyPageFromExport } from "./page-copy-service.js";
import { buildInspectPageReport } from "./page-inspect-report.js";
import type {
  PrimeUiComponentCandidatesInput,
  PrimeUiComponentCandidatesResult,
  PrimeUiComponentPropsValidateInput,
  PrimeUiComponentPropsValidateResult,
  PrimeUiCreateProjectApiPageInput,
  PrimeUiCreateProjectApiVariantInput,
  PrimeUiCopyComponentResult,
  PrimeUiCopyExportableResult,
  PrimeUiCopyPageResult,
  PrimeUiCopyRegistryComponentResult,
  PrimeUiCreateComponentExportResult,
  PrimeUiCreateExportResult,
  PrimeUiDownloadResult,
  PrimeUiExport,
  PrimeUiComponentExportManifest,
  PrimeUiManagedSearchEnvironmentResult,
  PrimeUiManagedSearchIndex,
  PrimeUiManagedSearchJob,
  PrimeUiManagedSearchOverviewResult,
  PrimeUiManagedSearchRuntimeDeleteResult,
  PrimeUiManagedSearchRuntimeMutationResult,
  PrimeUiManagedSearchRuntimeStatus,
  PrimeUiManagedSearchSyncResult,
  PrimeUiIssueReportSubmitResult,
  PrimeUiExportManifest,
  PrimeUiInspectPageResult,
  PrimeUiProjectApiPagesResult,
  PrimeUiProjectApiVariantResult,
  PrimeUiProjectApiVariantSummaryResult,
  PrimeUiProjectApiVariantsResult,
  PrimeUiProjectDescriptionResult,
  PrimeUiProjectInfo,
  PrimeUiProjectPageDetailsResult,
  PrimeUiSubmitIssueReportInput,
} from "../types.js";
import { copyExportableFromExport } from "./exportable-copy-service.js";
import { copyComponentFromExport } from "./component-copy-service.js";
import { copyRegistryComponentFromExport } from "./registry-component-copy-service.js";
import {
  buildExportManifestArtifact,
  buildReferenceExportArtifact,
} from "./runtime-guidance.js";

type ProjectSyncServiceOptions = {
  projectRoot: string;
  targetProjectRoot: string;
  provider: ProjectDataProvider;
  pageDetailsBySlug?: Map<string, PrimeUiProjectPageDetailsResult>;
};

function buildManifestPath(exportsRoot: string, exportId: string): string {
  return path.join(exportsRoot, `${exportId}.manifest.json`);
}

function normalizeSlug(slug: string): string {
  const trimmed = slug.trim();
  if (!trimmed || trimmed === "/") {
    return "/";
  }

  const withLeadingSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  return withLeadingSlash.replace(/\/+$/, "") || "/";
}

function normalizeEnvironmentSlug(slug: string): string {
  return slug.trim();
}

export class ProjectSyncService implements ProjectSyncSource {
  private readonly provider: ProjectDataProvider;
  private readonly projectRoot: string;
  private readonly targetProjectRoot: string;
  private readonly primeUiRoot: string;
  private readonly tempRoot: string;
  private readonly exportsRoot: string;
  private readonly pageDetailsBySlug: Map<
    string,
    PrimeUiProjectPageDetailsResult
  >;

  constructor(options: ProjectSyncServiceOptions) {
    this.projectRoot = options.projectRoot;
    this.targetProjectRoot = options.targetProjectRoot;
    this.provider = options.provider;
    this.pageDetailsBySlug = options.pageDetailsBySlug ?? new Map();
    this.primeUiRoot = path.join(this.projectRoot, ".primeui");
    this.tempRoot = path.join(this.primeUiRoot, "temp");
    this.exportsRoot = path.join(this.tempRoot, "exports");
  }

  async getProjectInfo(
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiProjectInfo> {
    await this.ensureTempLayout();
    return this.provider.getProjectInfo();
  }

  async getProjectDescription(
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiProjectDescriptionResult> {
    return this.provider.getProjectDescription();
  }

  async upsertProjectDescription(
    projectDescription: string,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiProjectDescriptionResult> {
    return this.provider.upsertProjectDescription(projectDescription);
  }

  async listProjectPages(
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiProjectApiPagesResult> {
    return this.provider.listProjectPages();
  }

  async getProjectPage(
    pageId: string,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiProjectApiPagesResult> {
    return this.provider.getProjectPage(pageId);
  }

  async createProjectPage(
    input: PrimeUiCreateProjectApiPageInput,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiProjectApiPagesResult> {
    return this.provider.createProjectPage(input);
  }

  async listProjectPageVariants(
    pageId: string,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiProjectApiVariantsResult> {
    return this.provider.listProjectPageVariants(pageId);
  }

  async getProjectPageVariant(
    pageId: string,
    variantId: string,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiProjectApiVariantResult> {
    return this.provider.getProjectPageVariant(pageId, variantId);
  }

  async createProjectPageVariant(
    pageId: string,
    input: PrimeUiCreateProjectApiVariantInput,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiProjectApiVariantSummaryResult> {
    return this.provider.createProjectPageVariant(pageId, input);
  }

  async setProjectPageActiveVariant(
    pageId: string,
    variantId: string,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiProjectApiPagesResult> {
    return this.provider.setProjectPageActiveVariant(pageId, variantId);
  }

  async submitIssueReport(
    input: PrimeUiSubmitIssueReportInput,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiIssueReportSubmitResult> {
    return this.provider.submitIssueReport(input);
  }

  async getSearchOverview(
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiManagedSearchOverviewResult> {
    return this.provider.getSearchOverview();
  }

  async getSearchEnvironment(
    environmentSlug: string,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiManagedSearchEnvironmentResult> {
    const normalizedSlug = normalizeEnvironmentSlug(environmentSlug);
    if (!normalizedSlug) {
      throw new Error("Search environment slug is required.");
    }

    const overview = await this.provider.getSearchOverview();
    const runtime = overview.runtimes.find(
      (candidate) => candidate.slug === normalizedSlug,
    );

    if (!runtime) {
      throw new Error(
        `Search environment "${normalizedSlug}" was not found in the current project overview.`,
      );
    }

    const relatedIndexes = this.getRelatedIndexesForRuntime(overview, runtime);
    const relatedIndexIds = new Set(relatedIndexes.map((index) => index.id));
    const primaryIndex = relatedIndexes[0] ?? null;

    return {
      success: true,
      environmentSlug: normalizedSlug,
      environment: runtime,
      primaryIndex,
      relatedIndexes,
      recentJobs: overview.recentJobs.filter((job) =>
        relatedIndexIds.has(job.projectSearchIndexId),
      ),
      recentSyncRuns: overview.recentSyncRuns.filter((syncRun) =>
        relatedIndexIds.has(syncRun.projectSearchIndexId),
      ),
      search: overview.search,
      runtimeModel: overview.runtimeModel,
      sources: overview.sources,
      lastError: overview.lastError,
    };
  }

  async createSearchEnvironment(
    input: {
      name: string;
      slug: string;
    },
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiManagedSearchRuntimeMutationResult> {
    const projectInfo = await this.provider.getProjectInfo();
    const result = await this.provider.createSearchEnvironment({
      name: input.name,
      projectId: projectInfo.projectId,
      slug: input.slug,
    });
    return {
      success: result.success,
      environment: result.runtime,
    };
  }

  async updateSearchEnvironment(
    input: {
      environmentSlug: string;
      name?: string;
      status?: PrimeUiManagedSearchRuntimeStatus;
    },
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiManagedSearchRuntimeMutationResult> {
    const projectInfo = await this.provider.getProjectInfo();
    const result = await this.provider.updateSearchEnvironment({
      projectId: projectInfo.projectId,
      runtimeSlug: input.environmentSlug,
      ...(input.name === undefined ? {} : { name: input.name }),
      ...(input.status === undefined ? {} : { status: input.status }),
    });
    return {
      success: result.success,
      environment: result.runtime,
    };
  }

  async deleteSearchEnvironment(
    environmentSlug: string,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiManagedSearchRuntimeDeleteResult> {
    const projectInfo = await this.provider.getProjectInfo();
    return this.provider.deleteSearchEnvironment({
      projectId: projectInfo.projectId,
      runtimeSlug: environmentSlug,
    });
  }

  async syncSearchEnvironment(
    environmentSlug: string,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiManagedSearchSyncResult> {
    const normalizedSlug = normalizeEnvironmentSlug(environmentSlug);
    if (!normalizedSlug) {
      throw new Error("Search environment slug is required.");
    }

    const packageJsonPath = path.join(this.targetProjectRoot, "package.json");
    const guidanceNotes = [
      `Run the command from ${this.targetProjectRoot}.`,
      "This tool returns guidance mode because Phase 13 keeps sync truthful to the real exported-project flow instead of pretending MCP generated a fresh payload itself.",
    ];

    try {
      const packageJson = JSON.parse(
        await readFile(packageJsonPath, "utf-8"),
      ) as {
        scripts?: Record<string, string>;
      };

      if (packageJson.scripts?.["search:sync"]) {
        return {
          success: true,
          mode: "guidance",
          environmentSlug: normalizedSlug,
          command: `pnpm search:sync --env ${normalizedSlug}`,
          workingDirectory: this.targetProjectRoot,
          jobId: null,
          status: null,
          notes: guidanceNotes,
        };
      }
    } catch {
      guidanceNotes.push(
        `Package script lookup at ${packageJsonPath} was unavailable, so MCP is falling back to the package CLI command.`,
      );
    }

    return {
      success: true,
      mode: "guidance",
      environmentSlug: normalizedSlug,
      command: `npx @primeuicom/cli search sync --env ${normalizedSlug}`,
      workingDirectory: this.targetProjectRoot,
      jobId: null,
      status: null,
      notes: guidanceNotes,
    };
  }

  async getComponentCandidates(
    input: PrimeUiComponentCandidatesInput,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiComponentCandidatesResult> {
    return this.provider.getComponentCandidates(input);
  }

  async validateComponentProps(
    input: PrimeUiComponentPropsValidateInput,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiComponentPropsValidateResult> {
    return this.provider.validateComponentProps(input);
  }

  async listExports(_context?: PrimeUiToolContext): Promise<PrimeUiExport[]> {
    await this.ensureTempLayout();
    return this.provider.listExports();
  }

  async createExport(
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiCreateExportResult> {
    await this.ensureTempLayout();
    const result = await this.provider.createExport();
    const manifestPath = buildManifestPath(this.exportsRoot, result.export.id);
    await writeUtf8(manifestPath, `${JSON.stringify(result, null, 2)}\n`);

    return {
      ...result,
      guidance: {
        artifacts: [buildExportManifestArtifact(manifestPath)],
        requiredActions: `Call download_export with id "${result.export.id}" next before attempting any file transfer.`,
      },
    };
  }

  async createComponentExport(
    componentId: string,
    options: { propsValidationId?: string } = {},
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiCreateComponentExportResult> {
    await this.ensureTempLayout();
    const result = await this.provider.createComponentExport(componentId, {
      propsValidationId: options.propsValidationId,
    });
    const manifestPath = buildManifestPath(this.exportsRoot, result.export.id);
    await writeUtf8(manifestPath, `${JSON.stringify(result, null, 2)}\n`);

    return {
      ...result,
      guidance: {
        artifacts: [buildExportManifestArtifact(manifestPath)],
        requiredActions: `Call download_component_export with id "${result.export.id}" next. This is a component-track manifest; do not use page copy tools until a component registry copy tool is available.`,
      },
    };
  }

  async downloadExportById(
    id: string,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiDownloadResult> {
    await this.ensureTempLayout();

    const exportsList = await this.provider.listExports();
    const selected = exportsList.find((item) => item.id === id);

    if (!selected) {
      throw new Error(`Export not found: ${id}`);
    }

    const targetZipPath = path.join(this.exportsRoot, `${id}.zip`);
    const targetProjectPath = path.join(this.exportsRoot, id);
    const manifestPath = buildManifestPath(this.exportsRoot, id);

    let manifest: PrimeUiExportManifest;
    try {
      manifest = parsePrimeUiExportManifest(
        JSON.parse(await readFile(manifestPath, "utf-8")) as unknown,
      );
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      throw new Error(
        `Export manifest is required for download id "${id}". Run create_export for this export before downloading. Details: ${reason}`,
      );
    }

    if (manifest.export.id !== id) {
      throw new Error(
        `Export manifest mismatch for id "${id}". Run create_export and retry download.`,
      );
    }

    await ensureDir(this.exportsRoot);
    await this.provider.downloadExportArchive(id, targetZipPath);

    await resetDir(targetProjectPath);
    await extractZip(targetZipPath, targetProjectPath);
    return {
      exportId: id,
      projectPath: targetProjectPath,
      manifestPath,
      guidance: {
        artifacts: [
          buildReferenceExportArtifact(targetProjectPath),
          buildExportManifestArtifact(manifestPath),
        ],
        requiredActions:
          "Use the export_manifest artifact as the source of truth for which files belong to the selected page, component, or exportable. Use the reference_export artifact as the golden example only when behavior diverges or integration is unclear. Then choose copy_page for full pages, copy_component for one selected block instance, or copy_exportable for shared exportables based on user intent.",
      },
    };
  }

  async downloadComponentExportById(
    id: string,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiDownloadResult> {
    await this.ensureTempLayout();

    const manifestPath = buildManifestPath(this.exportsRoot, id);
    const manifest = await this.readComponentExportManifest(id, manifestPath);
    const targetZipPath = path.join(this.exportsRoot, `${id}.zip`);
    const targetProjectPath = path.join(this.exportsRoot, id);

    await ensureDir(this.exportsRoot);
    await this.provider.downloadExportArchive(id, targetZipPath);

    await resetDir(targetProjectPath);
    await extractZip(targetZipPath, targetProjectPath);
    return {
      exportId: id,
      projectPath: targetProjectPath,
      manifestPath,
      guidance: {
        artifacts: [
          buildReferenceExportArtifact(targetProjectPath),
          buildExportManifestArtifact(manifestPath),
        ],
        requiredActions: `Use the component export_manifest artifact as the source of truth for component "${manifest.component.componentId}" and the reference_export artifact for inspection only. The next component-track step is a registry component copy tool; do not install the virtual page as a normal page.`,
      },
    };
  }

  async inspectPage(
    slug: string,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiInspectPageResult> {
    await this.ensureTempLayout();

    const pageDetails = await this.provider.getProjectPageBySlug(slug);
    const reportPayload = buildInspectPageReport(pageDetails);
    this.pageDetailsBySlug.set(normalizeSlug(slug), pageDetails);

    return {
      ...pageDetails,
      report: reportPayload.report,
      reportRows: reportPayload.reportRows,
    };
  }

  private async readComponentExportManifest(
    id: string,
    manifestPath: string,
  ): Promise<PrimeUiComponentExportManifest> {
    let manifest: PrimeUiComponentExportManifest;
    try {
      manifest = parsePrimeUiComponentExportManifest(
        JSON.parse(await readFile(manifestPath, "utf-8")) as unknown,
      );
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error);
      throw new Error(
        `Component export manifest is required for download id "${id}". Run create_component_export for this export before downloading. Details: ${reason}`,
      );
    }

    if (manifest.export.id !== id || manifest.exportId !== id) {
      throw new Error(
        `Component export manifest mismatch for id "${id}". Run create_component_export and retry download.`,
      );
    }

    return manifest;
  }

  async copyPage(
    originPageSlug: string,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiCopyPageResult> {
    await this.ensureTempLayout();

    return copyPageFromExport({
      projectRoot: this.targetProjectRoot,
      exportsRoot: this.exportsRoot,
      originPageSlug,
    });
  }

  async copyComponent(
    originPageSlug: string,
    blockId: string,
    componentId: string,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiCopyComponentResult> {
    await this.ensureTempLayout();

    const normalizedOriginSlug = normalizeSlug(originPageSlug);
    const cachedPageDetails = this.pageDetailsBySlug.get(normalizedOriginSlug);
    const pageDetails =
      cachedPageDetails ??
      (await this.provider.getProjectPageBySlug(originPageSlug));

    if (!cachedPageDetails) {
      this.pageDetailsBySlug.set(normalizedOriginSlug, pageDetails);
    }

    return copyComponentFromExport({
      projectRoot: this.targetProjectRoot,
      exportsRoot: this.exportsRoot,
      originPageSlug,
      blockId,
      componentId,
      pageDetails,
    });
  }

  async copyRegistryComponent(
    componentId: string,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiCopyRegistryComponentResult> {
    await this.ensureTempLayout();

    return copyRegistryComponentFromExport({
      projectRoot: this.targetProjectRoot,
      exportsRoot: this.exportsRoot,
      componentId,
    });
  }

  async copyExportable(
    exportableKey: string,
    _context?: PrimeUiToolContext,
  ): Promise<PrimeUiCopyExportableResult> {
    await this.ensureTempLayout();

    return copyExportableFromExport({
      projectRoot: this.targetProjectRoot,
      exportsRoot: this.exportsRoot,
      exportableKey,
    });
  }

  async clearTemp(_context?: PrimeUiToolContext): Promise<void> {
    await resetDir(this.tempRoot);
    await ensureDir(this.exportsRoot);
    this.pageDetailsBySlug.clear();
  }

  private async ensureTempLayout(): Promise<void> {
    await ensureDir(this.primeUiRoot);
    await ensureDir(this.tempRoot);
    await ensureDir(this.exportsRoot);
  }

  private getRelatedIndexesForRuntime(
    overview: PrimeUiManagedSearchOverviewResult,
    runtime: PrimeUiManagedSearchOverviewResult["runtimes"][number],
  ): PrimeUiManagedSearchIndex[] {
    const indexes: PrimeUiManagedSearchIndex[] = [];
    const seen = new Set<string>();

    const pushIndex = (index: PrimeUiManagedSearchIndex | null | undefined) => {
      if (!index || seen.has(index.id)) {
        return;
      }

      seen.add(index.id);
      indexes.push(index);
    };

    if (overview.defaultIndex) {
      const matchesByUid =
        runtime.currentMeilisearchIndexUid &&
        overview.defaultIndex.meilisearchIndexUid ===
          runtime.currentMeilisearchIndexUid;
      const matchesDefaultSlug =
        runtime.slug === overview.runtimeModel.defaultRuntimeSlug &&
        overview.defaultIndex.kind === "default-runtime";

      if (matchesByUid || matchesDefaultSlug) {
        pushIndex(overview.defaultIndex);
      }
    }

    for (const index of overview.customIndexes) {
      if (
        runtime.currentMeilisearchIndexUid &&
        index.meilisearchIndexUid === runtime.currentMeilisearchIndexUid
      ) {
        pushIndex(index);
      }
    }

    return indexes;
  }
}
